import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Os campos que uma coluna da planilha pode preencher no contato. Espelha `PessoaImportacao` na API. */
export interface PessoaImportacao {
    nome?: string;
    telefone?: string;
    email?: string;
    segmento?: string;
    cep?: string;
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
}

/** Espelha `OrganizacaoImportacao`. Sem CNPJ válido a organização não nasce. */
export interface OrganizacaoImportacao {
    cnpj?: string;
    razaoSocial?: string;
    nomeFantasia?: string;
    site?: string;
    email?: string;
    telefone?: string;
    segmento?: string;
    cep?: string;
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
}

export interface LinhaImportacao {
    /** Número da linha na planilha (o cabeçalho é a 1). */
    linha: number;
    pessoa?: PessoaImportacao;
    organizacao?: OrganizacaoImportacao;
}

export type CodigoAviso =
    | 'sem-contato' | 'telefone-invalido' | 'email-invalido'
    | 'sem-cnpj' | 'cnpj-invalido' | 'cnpj-sem-nome'
    | 'telefone-excluido' | 'outra-organizacao' | 'linha-vazia';

export interface AvisoImportacao {
    linha: number;
    codigo: CodigoAviso;
    mensagem: string;
    /** true quando nada da linha foi gravado. */
    ignorada: boolean;
}

export interface ResultadoImportacao {
    simulado: boolean;
    totalLinhas: number;
    contatosNovos: number;
    contatosCompletados: number;
    contatosSemMudanca: number;
    organizacoesNovas: number;
    organizacoesCompletadas: number;
    organizacoesSemMudanca: number;
    vinculos: number;
    linhasIgnoradas: number;
    avisos: AvisoImportacao[];
}

/** O mesmo limite da API (`ImportacaoContatoService.LimiteLinhas`). */
export const LIMITE_LINHAS = 5000;

export const ImportacaoService = {
    /**
     * `simular: true` devolve a prévia sem gravar; `false` grava. A API passa as duas pelo mesmo
     * plano, então a prévia é o que a gravação faz. Só o administrador (403 para os outros).
     */
    importar: async (linhas: LinhaImportacao[], simular: boolean): Promise<ResultadoImportacao> => {
        const response = await apiFetch(`${API_URL}/api/Contato/Importar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ simular, linhas }),
        });

        if (response.status === 403) throw new Error('Só o administrador da empresa pode importar planilhas.');

        const corpo = await response.json().catch(() => null);
        if (!response.ok || !corpo?.sucesso) {
            throw new Error(corpo?.mensagem || 'Não foi possível importar a planilha.');
        }
        return corpo.dados as ResultadoImportacao;
    },
};
